// src/service/SessionManager.js
import { SESSION_TIMEOUT_MINUTES as CONFIG_SESSION_TIMEOUT } from '../util/config';
import SessionInvalidationService from './SessionInvalidationService';

class SessionManager {
  // Use configurable session timeout (5-30 min range, default 15 min)
  static SESSION_TIMEOUT_MINUTES = CONFIG_SESSION_TIMEOUT;
  static REFRESH_MARGIN_SECONDS = 60; // refresh 1 min before expiry

  static _accessToken = null;
  static _refreshToken = null;
  static _lastActivity = null;
  static _timeoutHandle = null;
  static _sessionId = null;

  static setSession(accessToken, refreshToken) {
    this._accessToken = accessToken;
    this._refreshToken = refreshToken;
    this._lastActivity = Date.now();
    
    // Generate and store session ID for single active session enforcement
    if (!this._sessionId) {
      this._sessionId = SessionInvalidationService.generateSessionId();
      SessionInvalidationService.setCurrentSessionId(this._sessionId);
      // Broadcast new session to invalidate other sessions
      SessionInvalidationService.broadcastNewSession(this._sessionId);
    }
    
    this._setSessionTimeout();
  }

  static getAccessToken() {
    this._lastActivity = Date.now();
    return this._accessToken;
  }

  static getRefreshToken() {
    return this._refreshToken;
  }

  static clearSession() {
    this._accessToken = null;
    this._refreshToken = null;
    this._lastActivity = null;
    this._sessionId = null;
    
    // Clear session ID from storage
    SessionInvalidationService.clearCurrentSessionId();
 
    if (this._timeoutHandle) clearTimeout(this._timeoutHandle);
  }

  // Set session timeout
  static _setSessionTimeout() {
    if (this._timeoutHandle) clearTimeout(this._timeoutHandle);
    this._timeoutHandle = setTimeout(() => {
      this.handleSessionTimeout();
    }, this.SESSION_TIMEOUT_MINUTES * 60 * 1000);
  }

  // Handle session timeout
  static handleSessionTimeout() {
    this.clearSession();
    // Optionally, redirect to login or show timeout message
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
  }

  // Validate session timeout (call on user activity)
  static validateSessionTimeout() {
    if (!this._lastActivity) return false;
    const now = Date.now();
    const elapsed = (now - this._lastActivity) / 1000 / 60; // minutes
    if (elapsed > this.SESSION_TIMEOUT_MINUTES) {
      this.handleSessionTimeout();
      return false;
    }
    this._setSessionTimeout();
    return true;
  }

  // Automatic token refresh (call before API calls)
  static async refreshTokenIfNeeded(handleRefreshToken) {
    const token = this.getAccessToken();
    if (!token) return;
    const payload = this._parseJwt(token);
    if (!payload || !payload.exp) return;
    const now = Math.floor(Date.now() / 1000);
    if (payload.exp - now < this.REFRESH_MARGIN_SECONDS) {
      // Token is about to expire, refresh
      const refreshToken = this.getRefreshToken();
      if (refreshToken && handleRefreshToken) {
        const result = await handleRefreshToken(refreshToken);
        
        // Check if refresh failed due to session invalidation (401 or 400)
        if (result === 401 || result === 400) {
          // Session was invalidated (likely due to concurrent login)
          const sessionId = this._sessionId || SessionInvalidationService.getCurrentSessionId();
          if (sessionId) {
            SessionInvalidationService.broadcastSessionInvalidation(
              sessionId,
              'Your session was invalidated because you logged in on another device or browser.'
            );
          }
          this.handleSessionInvalidation('Your session was invalidated because you logged in on another device or browser.');
          return;
        }
        
        if (result && result.access_token && result.refresh_token) {
          this.setSession(result.access_token, result.refresh_token);
        } else {
          this.handleSessionTimeout();
        }
      }
    }
  }

  // Handle session invalidation (different from timeout - user was logged out due to concurrent login)
  static handleSessionInvalidation(reason) {
    this.clearSession();
    // Store reason for display
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('rms_session_invalidation_reason', reason || 'Your session was invalidated.');
    }
    // Redirect to login
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
  }

  // Parse JWT token
  static _parseJwt(token) {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (e) {
      return null;
    }
  }
}

export default SessionManager; 